import {
  TextField,
  Typography,
  Box,
  Grid,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useLocation } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';

import Container from "@mui/material/Container";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import { styled } from "@mui/material/styles";
import * as React from "react";
import { useState, useEffect } from "react";
import {
  getUserEmail,
  getUserName,
} from "../utils/sessionCheck"; 
import { handleUpload } from "../DataUploader";
import {
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { firestore } from "../firebase.config";
import CircularProgress from "@mui/material/CircularProgress";
import PageNavBar from '../components/PageNavBar';
import { calculateDistance, getUserGeolocation } from "../utils/geolocationUtils";
import Footer from "../components/Footer";
import TimePicker from "./TimePicker";
import { collection, getDocs, docId, query, where, onSnapshot } from "firebase/firestore";
import { useUserContext } from "../context/UserContext";
import TenantBanner from "../components/TenantBanner";

// // Common styles for floating cards
// const COMMON_CARD_STYLES = {
//   width: 120,
//   height: 120,
//   p: 2,
//   borderRadius: "24px",
//   background: "rgba(255,255,255,0.08)",
//   border: "1px solid rgba(255,255,255,0.6)",
//   backdropFilter: "blur(8px)",
//   display: "flex",
//   flexDirection: "column",
//   alignItems: "center",
//   justifyContent: "center",
// };

// // Common text styles for card labels
// const COMMON_LABEL_STYLES = {
//   color: "#ffffffff",
//   fontWeight: 500,
//   fontSize: "1rem",
// };

// // Common text styles for card values
// const COMMON_VALUE_STYLES = {
//   color: "#ffffffff",
//   fontWeight: 700,
//   fontSize: "1.2rem",
// };

// // Card configurations with their specific properties
// const FLOATING_CARDS = [
//   {
//     id: "streak",
//     position: { top: 150, left: 40 },
//     boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
//     label: "Streak",
//     value: "5 Days",
//     labelStyles: {
//       fontFamily: "Inter, sans-serif",
//       fontWeight: 200,
//       fontSize: "1.2rem",
//       lineHeight: 1.2,
//     },
//     valueStyles: {
//       fontFamily: "Inter, sans-serif",
//       fontWeight: 700,
//       fontSize: "1.2rem",
//       lineHeight: 1.2,
//     }
//   },
//   {
//     id: "tasksDone",
//     position: { top: 500, left: 90 },
//     boxShadow: "0 8px 32px rgba(124,58,237,0.15)",
//     label: "Tasks Done",
//     value: "12/15"
//   },
//   {
//     id: "productivity",
//     position: { bottom: 100, right: 60 },
//     boxShadow: "0 8px 32px rgba(59,130,246,0.15)",
//     label: "Productivity",
//     value: "85%",
//     valueStyles: { fontSize: "1.4rem" }
//   },
//   {
//     id: "focusTime",
//     position: { top: 90, right: 200 },
//     boxShadow: "0 8px 32px rgba(231,58,237,0.13)",
//     label: "Focus Time",
//     value: "2h 45m"
//   },
//   {
//     id: "energy",
//     position: { top: 300, right: 120 },
//     boxShadow: "0 8px 32px rgba(231,58,237,0.13)",
//     label: "Energy",
//     value: "High",
//     valueStyles: { fontSize: "1.4rem" }
//   }
// ];

// Styled variant of IconButton with outlined style
const CustomOutlinedIconButton = styled(IconButton)(({ theme }) => ({
  border: `1px solid ${theme.palette.text.primary}`, // Black border
  "&:hover": {
    backgroundColor: "transparent", // Transparent background on hover
  },
}));

const AssignTimer = () => {
  const navigate = useNavigate();
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [startButtonDisabled, setStartButtonDisabled] = useState(false);
  const [taskError, setTaskError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isOutsideRadius, setIsOutsideRadius] = useState(true);
  const [geoCheckDone, setGeoCheckDone] = useState(false);
  const [geolocationAuthEnabled, setGeolocationAuthEnabled] = useState(true); // Default is true
  const [startTimestamp, setStartTimestamp] = useState(null);
  const [value, setValue] = useState(""); 
  const [taskInput, setTaskInput] = useState(""); // Holds the assigned task name

  const [timerDocId, setTimerDocId] = useState(null);
  const [stopButtonDisabled, setStopButtonDisabled] = useState(false); // Initially false
  const location = useLocation();
  const { userId } = location.state || {};
  const [tasks, setTasks] = useState([]);
  const [activeTask, setActiveTask] = useState(null);
  const [timeElapsed, setTimeElapsed] = useState(0);
  
  const { userRole, tenantId, selectedTenantId } = useUserContext();
  const tenantFilterID = userRole === "SuperAdmin" 
      ? selectedTenantId || sessionStorage.getItem("selectedTenantId") 
      : tenantId || sessionStorage.getItem("selectedTenantId");

  useEffect(() => {
    const storedDocId = localStorage.getItem("docId_store");
    if (storedDocId) {
      setTimerDocId(storedDocId);    }
  }, []);

  const userEmail = getUserEmail();
  const userName = getUserName();

  // Fetch geolocation settings from Firestore
  useEffect(() => {
    const fetchGeolocationSettings = async () => {
      try {
        const settingsDocRef = doc(firestore, "Settings", "geolocationAuth");
        const settingsDoc = await getDoc(settingsDocRef);
  
        if (settingsDoc.exists()) {
          const settingsData = settingsDoc.data();
          setGeolocationAuthEnabled(settingsData.enabled);
        } else {
          console.error("Geolocation settings not found.");
          setGeolocationAuthEnabled(false);  // Allow normal access if no settings found
        }
      } catch (error) {
        console.error("Error fetching geolocation settings:", error);
        setGeolocationAuthEnabled(false);  // Allow normal access on error
      } finally {
        setIsLoading(false); // Stop loading once settings are fetched
      }
    };
  
    fetchGeolocationSettings();
  }, []);

  // Check user distance and update UI accordingly
  const checkUserDistance = async () => {
    if (!geolocationAuthEnabled) {
      setGeoCheckDone(true);
      setIsOutsideRadius(false); // Allow normal access if geolocation is disabled
      return;
    }
  
    try {
      const userGeolocation = await getUserGeolocation();
      const userLocation = { 
        latitude: userGeolocation.latitude, 
        longitude: userGeolocation.longitude 
      };
  
      // Fetch user document from Firestore using email
      const userDocRef = doc(firestore, "Users", userEmail);
      const userDoc = await getDoc(userDocRef);
  
      if (userDoc.exists()) {
        const { latitude, longitude, radius } = userDoc.data().coordinates;
  
        const distance = calculateDistance(userLocation.latitude, userLocation.longitude, latitude, longitude);
        setIsOutsideRadius(distance > radius); // true if outside, false if inside
      } else {
        console.error("No user data found.");
        setIsOutsideRadius(true); // Default to outside if no user data
      }
    } catch (error) {
      console.error("Error checking user distance:", error);
      setIsOutsideRadius(true); // Default to outside on error
    } finally {
      setGeoCheckDone(true); // Mark that geolocation check is done
    }
  };
  

  useEffect(() => {
    if (geolocationAuthEnabled) {
      checkUserDistance();
    } else {
      setGeoCheckDone(true);
      setIsOutsideRadius(false); // Allow normal access if geolocation is disabled
    }
  }, [geolocationAuthEnabled]);

  // Remove duplicate timer effects and consolidate into one
  useEffect(() => {
    let interval;
  
    if (timerRunning && startTimestamp) {
      // Initial update
      const now = new Date();
      const elapsedMillis = now.getTime() - startTimestamp.getTime();
      const elapsedSeconds = Math.floor(elapsedMillis / 1000);
      setElapsedTime(elapsedSeconds);
      
      // Set up interval for updates
      interval = setInterval(() => {
        const now = new Date();
        const elapsedMillis = now.getTime() - startTimestamp.getTime();
        const elapsedSeconds = Math.floor(elapsedMillis / 1000);
        setElapsedTime(elapsedSeconds);
        
        // Store in localStorage
        localStorage.setItem("elapsedTime", elapsedSeconds.toString());
        
        // Auto-stop after 8 hours
        if (elapsedSeconds >= 28800) {
          stopTimer();
          setSuccessMessage("Timer stopped automatically at 8 hours");
        }
      }, 1000);
    } else {
      // If timer is not running, clear the elapsed time
      setElapsedTime(0);
      localStorage.removeItem("elapsedTime");
    }
  
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [timerRunning, startTimestamp]);

  // Function to calculate total time in minutes -- Added changes since it was showing invalid start time
  const calculateTotalTime = (start, end) => {
  if (!(start instanceof Date) || isNaN(start.getTime()) ||
      !(end instanceof Date) || isNaN(end.getTime())) {
    console.error("Invalid start or end time:", start, end);
    return 0;
  }

  const totalTimeMillis = end.getTime() - start.getTime();
  const totalTimeMinutes = Math.floor(totalTimeMillis / (1000 * 60));
  return totalTimeMinutes;
};
  
  useEffect(() => {
    const savedStartTime = localStorage.getItem("startTimestamp");
    const savedTimerRunning = localStorage.getItem("timerRunning");
    const savedTask = localStorage.getItem("activeTask");
  
    if (savedStartTime && savedTimerRunning === "true") {
      const restoredStartTime = new Date(savedStartTime);
      if(!isNaN(restoredStartTime.getTime())) {
      setStartTimestamp(restoredStartTime);
      setTimerRunning(true);
      setStopButtonDisabled(false); // ✅ Ensure stop button is enabled
      }
      else{
        console.error("Invalid start time in localStorage:", savedStartTime);
      }
  
      // Restore elapsed time
      const now = new Date();
      setElapsedTime(Math.floor((now.getTime() - restoredStartTime.getTime()) / 1000));
  
      // Restore the active task
      if (savedTask) {
        setActiveTask(JSON.parse(savedTask)); // ✅ Restore task from localStorage
      }
    }
  }, []);
  

  const fetchAssignedTasks = async () => {
    try {
      const tasksCollection = collection(firestore, "assignedTasks");
      // const taskSnapshot = await getDocs(tasksCollection);
      const q = query(
        collection(firestore, "assignedTasks"),
        where("assignedTo", "==", userEmail),
        where("tenantId", "==", tenantFilterID)
      );
      const taskSnapshot = await getDocs(q);
      const userTasksQuery = query(
        collection(firestore, "assignedTasks"),
        where("assignedTo", "==", userEmail),
        where("tenantId", "==", tenantFilterID)
      );


      const taskList = taskSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter tasks for the current user
      const userTasks = taskList.filter(task => task.assignedTo === userEmail);
      
      // Sort tasks by start time
      userTasks.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
      
      setTasks(userTasks);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching assigned tasks:", error);
      setIsLoading(false);
    }
  };

  // Update the useEffect to fetch tasks when userEmail changes
  useEffect(() => {
    if (userEmail) {
      fetchAssignedTasks();
    }
  }, [userEmail]);
  

  useEffect(() => {
    const checkForActiveTask = () => {
      const now = new Date();
      
      // First check localStorage for any saved active task
      const savedActiveTask = localStorage.getItem("activeTask");
      if (savedActiveTask) {
        try {
          const parsedTask = JSON.parse(savedActiveTask);
          setActiveTask(parsedTask);
          setTaskInput(parsedTask.taskDescription);
          
          if (parsedTask.isRunning && !timerRunning) {
            let savedStartTime;
            try {
              if (parsedTask.originalStartTime) {
                savedStartTime = new Date(parsedTask.originalStartTime);
              } else if (parsedTask.startTime && parsedTask.startTime.includes('T')) {
                savedStartTime = new Date(parsedTask.startTime);
              } else {
                savedStartTime = new Date();
                console.warn('No valid start time found, using current time');
              }
            } catch (error) {
              console.error('Error parsing date:', error);
              savedStartTime = new Date();
            }

            setStartTimestamp(savedStartTime);
            setTimerRunning(true);
            localStorage.setItem("startTimestamp", savedStartTime.toISOString());
            localStorage.setItem("timerRunning", "true");
          }
          return;
        } catch (error) {
          console.error("Error parsing saved task:", error);
          localStorage.removeItem("activeTask");
        }
      }
      
      // Find task that should start based on assigned start time
      const taskToStart = tasks.find(task => {
        try {
          const assignedStartTime = new Date(task.startTime);
          const currentTime = new Date();
          
          // Check if we're within the first minute after the start time
          const timeDifference = currentTime - assignedStartTime;
          const isWithinStartWindow = timeDifference >= 0 && timeDifference <= 60000; // Within first minute
          
          return isWithinStartWindow && 
                 !task.isRunning && 
                 task.status !== "Completed";
        } catch (error) {
          console.error("Error parsing task start time:", error);
          return false;
        }
      });
  
      if (taskToStart) {
        // Use the scheduled start time instead of current time
        const scheduledStartTime = new Date(taskToStart.startTime);
        const formattedStartTime = formatTimeForDisplay(scheduledStartTime);
        
        const updatedTask = { 
          ...taskToStart, 
          isRunning: true,
          // startTime: formattedStartTime.toISOString(),
           startTime: scheduledStartTime.toISOString(),
          originalStartTime: scheduledStartTime.toISOString()
        };
        
        setActiveTask(updatedTask);
        setTaskInput(updatedTask.taskDescription);
        setTimeElapsed(0);
        setStartTimestamp(scheduledStartTime);
        setTimerRunning(true);
        
        // Save to localStorage
        localStorage.setItem("activeTask", JSON.stringify(updatedTask));
        localStorage.setItem("startTimestamp", scheduledStartTime.toISOString());
        localStorage.setItem("timerRunning", "true");
  
        // Update Firestore
        const taskDocRef = doc(firestore, "assignedTasks", taskToStart.id);
        setDoc(taskDocRef, { ...updatedTask }, { merge: true })
          .catch(error => console.error("Error updating task status:", error));
      }
    };
  
    checkForActiveTask();
    const interval = setInterval(checkForActiveTask, 1000);
    return () => clearInterval(interval);
  }, [tasks, timerRunning]);
  
  
  
  
  useEffect(() => {
    let interval;
  
    if (timerRunning && startTimestamp) {
      interval = setInterval(() => {
        const now = new Date();
        const elapsedMillis = now.getTime() - startTimestamp.getTime(); // Calculate the difference in milliseconds
        const elapsedSeconds = Math.floor(elapsedMillis / 1000); // Convert to seconds
        setElapsedTime(elapsedSeconds);
        localStorage.setItem("elapsedTime", elapsedSeconds); // ✅ Save in localStorage
        localStorage.setItem("timerRunning", "true"); // ✅ Save timer state

        // Automatically stop after 8 hours (14,400 seconds)
        if (elapsedSeconds >= 14400) {
          stopTimer(); // Automatically stop the timer
          setSuccessMessage("Timer stopped automatically at 8 hours");
        }
      }, 1000);
    } else {
      clearInterval(interval);
    }
  
    return () => clearInterval(interval);
  }, [timerRunning, startTimestamp]);
  
  // Retrieve elapsed time on mount
  useEffect(() => {
    const storedElapsedTime = localStorage.getItem("elapsedTime");
    if (storedElapsedTime) {
      setElapsedTime(parseInt(storedElapsedTime, 10));
    }
  }, []);
  
  useEffect(() => {
    const storedTimerRunning = localStorage.getItem("timerRunning") === "true";
    if (storedTimerRunning) {
      setTimerRunning(true);
    }
  }, []);


  const formatTimeForDisplay = (date) => {
    if (!(date instanceof Date)) {
      date = new Date(date);
    }
    //New changes
      if (isNaN(date.getTime())) {
    return "Invalid Time";
  }
    
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    
    // Convert 24h to 12h format
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    
    // Add leading zeros
    const formattedHours = hours.toString().padStart(2, '0');
    const formattedMinutes = minutes.toString().padStart(2, '0');
    const formattedSeconds = seconds.toString().padStart(2, '0');
    
    return `${formattedHours}:${formattedMinutes}:${formattedSeconds} ${ampm}`;
  };

  const stopTimer = async () => {
    if (!activeTask) {
      console.error("No active task to stop!");
      return;
    }

    try {
      const endTime = new Date();
      const formattedEndTime = formatTimeForDisplay(endTime);
      const totalTime = calculateTotalTime(startTimestamp, endTime);

      // Update Firestore with both formatted and original time
      const taskDocRef = doc(firestore, "assignedTasks", activeTask.id);
      await setDoc(taskDocRef, {
        ...activeTask,
        tenantId: tenantFilterID,
        tenantName: userRole === "SuperAdmin" ? sessionStorage.getItem("selectedTenantName") : userName,
        isRunning: false,
        endTime: formattedEndTime,
        originalEndTime: endTime.toISOString(),
        totalTimeBooked: `${totalTime} minutes`,
        status: "Completed",
        lastUpdated: endTime.toISOString()
      }, { merge: true });

      // Reset local state
      setActiveTask(null);
      setStartTimestamp(null);
      setTaskInput("");
      setTimeElapsed(0);
      setElapsedTime(0);
      setStopButtonDisabled(true);
      setTimerRunning(false);

      // Clear localStorage
      localStorage.removeItem("startTimestamp");
      localStorage.removeItem("timerRunning");
      localStorage.removeItem("activeTask");
      localStorage.removeItem("elapsedTime");

      setSuccessMessage("Task completed successfully!");
    } catch (error) {
      console.error("Error stopping task:", error);
      toast.error("Error completing task. Please try again.");
    }
  };

  const formatTime = (timeInSeconds) => {
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const seconds = timeInSeconds % 60;

    return `${hours}:${minutes < 10 ? "0" : ""}${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const getUTCDate = (date) => {
    const utcYear = date.getUTCFullYear();
    const utcMonth = date.getUTCMonth() + 1;
    const utcDay = date.getUTCDate();
    const twoDigitYear = utcYear % 100;
    const utcDayString = utcDay < 10 ? `0${utcDay}` : utcDay.toString();
    const utcMonthString = utcMonth < 10 ? `0${utcMonth}` : utcMonth.toString();
    return `${utcDayString}-${utcMonthString}-${twoDigitYear}`;
  };

  const getTime = (date) => {
    const Hour = date.getHours();
    const Minute = date.getMinutes();
    const Second = date.getSeconds();
    const am_pm = Hour >= 12 ? "PM" : "AM";
    return `${Hour}:${Minute}:${Second} ${am_pm}`;
  };

  // Add real-time listener for assigned tasks
  useEffect(() => {
    if (!userEmail) return;

    const tasksCollection = collection(firestore, "assignedTasks");
    const userTasksQuery = query(tasksCollection, where("assignedTo", "==", userEmail));
    
    const unsubscribe = onSnapshot(userTasksQuery, (snapshot) => {
      const updatedTasks = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort tasks by start time
      updatedTasks.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
      setTasks(updatedTasks);

      // Check for active task
      const activeTask = updatedTasks.find(task => task.isRunning);
      if (activeTask) {
        setActiveTask(activeTask);
        setTaskInput(activeTask.taskDescription);
        
        if (activeTask.startTime) {
          const startTime = new Date(activeTask.startTime);
          setStartTimestamp(startTime);
          setTimerRunning(true);
          
          // Calculate elapsed time
          const now = new Date();
          const elapsedSeconds = Math.floor((now.getTime() - startTime.getTime()) / 1000);
          setElapsedTime(elapsedSeconds);
        }
      } else {
        setActiveTask(null);
        setTaskInput("");
        setTimerRunning(false);
        setElapsedTime(0);
      }
    }, (error) => {
      console.error("Error listening to tasks:", error);
      toast.error("Error syncing tasks");
    });

    return () => unsubscribe();
  }, [userEmail]);

  // Add cleanup effect
  useEffect(() => {
    return () => {
      // Cleanup when component unmounts
      setActiveTask(null);
      setTimerRunning(false);
      setElapsedTime(0);
      setTaskInput("");
    };
  }, []);

  return (
    <Box sx={{ 
      minHeight: "100vh",
      background: "linear-gradient(135deg, #3b82f6 0%, #7c3aed 50%, rgb(231, 58, 237) 100%)",
      display: "flex",
      flexDirection: "column",
      position: "relative",
      overflow: "hidden",
    }}>
      <Toaster position="top-right" />
      <PageNavBar title="Assigned Timer" backTo="/dashboard" />

      <TenantBanner />
      
      {/* Floating Message Cards
      {FLOATING_CARDS.map((card) => (
        <Box
          key={card.id}
          sx={{
            position: "absolute",
            ...card.position,
            ...COMMON_CARD_STYLES,
            boxShadow: card.boxShadow,
          }}
        >
          <Typography
            sx={{
              ...COMMON_LABEL_STYLES,
              ...(card.labelStyles || {}),
            }}
          >
            {card.label}
          </Typography>

          <Typography
            sx={{
              ...COMMON_VALUE_STYLES,
              ...(card.valueStyles || {}),
            }}
          >
            {card.value}
          </Typography>
        </Box>
      ))} */}

      <Container sx={{ 
        pb: 8, 
        flex: 1, 
        display: "flex", 
        flexDirection: "column", 
        justifyContent: "center", 
        px: { xs: 2, sm: 3, md: 4 } 
      }}>
        
        {/* White Card Box: Local Time → Timer → Stop Button */}
        <Box
        sx={{
          backgroundColor: "white",
          borderRadius: { xs: 4, sm: 6, md: 8 },
          p: { xs: 2, sm: 3, md: 4 },
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          width: {
            xs: "100%",
            sm: "85%",
            md: "70%",
            lg: "50%",
          },
          maxWidth: { xs: "100%", sm: 600, md: 700, lg: 900 },
          minHeight: { xs: 300, sm: 350, md: 400 },
          mx: { xs: 2, sm: "auto" },  // <-- add small margin on mobile
          mt: { xs: 1, sm: 2, md: 3 },
          alignSelf: "center",
        }}
      >
      
        
        {/*TimePicker Component*/}
        <Box sx={{ textAlign: "center", mt: { xs: 1, sm: 2 } }}>
          <Typography 
            variant="h6" 
            sx={{ 
              color: "black",
              fontSize: { xs: "1rem", sm: "1.25rem" }
            }}
          >
            Local Time:
          </Typography>
          <TimePicker />
        </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: { xs: 1, sm: 2 } }}>
          <CircularProgress sx={{ color: "white" }} />
        </Box>
      ) : null}

      <Box component="form" sx={{ textAlign: "center", mt: { xs: 2, sm: 3, md: 4 } }}>
        <Typography 
          variant="h6" 
          sx={{ 
            color: "black",
            fontSize: { xs: "1rem", sm: "1.25rem" }
          }}
        >
          Timer: {elapsedTime ? formatTime(elapsedTime) : "00:00:00"}
        </Typography>

        {!geoCheckDone ? (
          <Typography sx={{ color: "black" }}>Checking geolocation...</Typography>
        ) : (
          <>
            {geolocationAuthEnabled && isOutsideRadius ? (
              <Typography sx={{ 
                mt: { xs: 1, sm: 2 }, 
                color: "#d32f2f", 
                fontWeight: "bold",
                fontSize: { xs: "0.875rem", sm: "1rem" }
              }}>
                You can't log time; you are outside the specified area.
              </Typography>
            ) : (
              <>
                
                <TextField
                  margin="normal"
                  label="Assigned Task"
                  variant="outlined"
                  fullWidth
                  name="tasks"
                  value={taskInput} // ✅ Auto-fill with assigned task
                  disabled // ✅ Prevent user from changing it
                  sx={{ 
                    mt: { xs: 1, sm: 2 }, 
                    mb: { xs: 1, sm: 2 },
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }
                  }}
                />
                {taskError && (
                  <Typography sx={{ 
                    mb: { xs: 1, sm: 2 }, 
                    color: "#d32f2f", 
                    fontWeight: "bold",
                    fontSize: { xs: "0.875rem", sm: "1rem" }
                  }}>
                    {taskError}
                  </Typography>
                )}
                <Grid container spacing={{ xs: 1, sm: 2 }} justifyContent="center">
                 
                  <Grid item>
                    

                    <CustomOutlinedIconButton
                    color="secondary"
                    onClick={stopTimer}
                    disabled={!activeTask} // ✅ Disable unless a task is active
                    sx={{
                      width: { xs: 48, sm: 56 },
                      height: { xs: 48, sm: 56 }
                    }}
                    >
                    <StopIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                    </CustomOutlinedIconButton>

                    </Grid>
                </Grid>
                {successMessage && (
                  <Typography sx={{ 
                    mt: { xs: 1, sm: 2 }, 
                    color: "#2e7d32", 
                    fontWeight: "bold",
                    fontSize: { xs: "0.875rem", sm: "1rem" }
                  }}>
                    {successMessage}
                  </Typography>
                )}
              </>
            )}
          </>
        )}
      </Box>
      {/* </Box> */}
      <Box sx={{ textAlign: "center", mt: { xs: 2, sm: 3, md: 4 } }}>
        <Typography 
          variant="h6" 
          sx={{ 
            color: "black",
            fontSize: { xs: "1rem", sm: "1.25rem" }
          }}
        >
          Ongoing Task:
        </Typography>
        {activeTask ? (
          <Typography 
            variant="body1" 
            sx={{ 
              color: "#2e7d32", 
              fontWeight: "bold",
              fontSize: { xs: "0.875rem", sm: "1rem" },
              px: { xs: 1, sm: 2 }
            }}
          >
            {activeTask.taskDescription} - Started at {formatTimeForDisplay(activeTask.startTime)}
          </Typography>
        ) : (
          <Typography 
            variant="body1" 
            sx={{ 
              color: "#d32f2f", 
              fontWeight: "bold",
              fontSize: { xs: "0.875rem", sm: "1rem" }
            }}
          >
            No ongoing task.
          </Typography>
        )}
      </Box>
        </Box>

      {/* Tasks Section - Responsive Layout */}
      <Box sx={{ 
        mt: { xs: 4, sm: 5, md: 6 }, 
        px: { xs: 1, sm: 2, md: 3, lg: 4 } 
      }}>
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} justifyContent="center">
          {/* Upcoming Tasks - Responsive Layout */}
          <Grid item xs={6} sm={6} md={6} lg={5}>
            <Box sx={{ textAlign: "center" }}>
              <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                  color: "white", 
                  mb: { xs: 1, sm: 2 },
                  fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" }
                }}
              >
                Upcoming Tasks
              </Typography>

              <Box sx={{
                maxHeight: { xs: "180px", sm: "200px", md: "250px" },
                overflowY: "auto",
                border: "1px solid #ccc",
                borderRadius: "8px",
                p: { xs: 1, sm: 1.5, md: 2 },
                backgroundColor: "#f9f9f9",
                minHeight: { xs: "120px", sm: "150px", md: "200px" },
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
              }}>
                {tasks.length > 0 ? (
                  tasks
                    .filter((task) => new Date(task.startTime) > new Date() && !task.isRunning)
                    .map((task) => (
                      <Box key={task.id} sx={{ 
                        p: { xs: 0.5, sm: 0.75, md: 1 }, 
                        borderBottom: "1px solid #ddd" 
                      }}>
                        <Typography 
                          variant="body1" 
                          fontWeight="bold"
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" } }}
                        >
                          {task.taskDescription}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="textSecondary"
                          sx={{ fontSize: { xs: "0.625rem", sm: "0.75rem", md: "0.875rem" } }}
                        >
                          Starts at {formatTimeForDisplay(new Date(task.startTime))}
                        </Typography>
                      </Box>
                    ))
                ) : (
                  <Typography 
                    variant="body1" 
                    color="error"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" } }}
                  >
                    No upcoming tasks.
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Past Tasks - Responsive Layout */}
          <Grid item xs={6} sm={6} md={6} lg={5}>
            <Box sx={{ textAlign: "center" }}>
              <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                  color: "white", 
                  mb: { xs: 1, sm: 2 },
                  fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" }
                }}
              >
                Past Tasks
              </Typography>

              <Box sx={{
                maxHeight: { xs: "180px", sm: "200px", md: "250px" },
                overflowY: "auto",
                border: "1px solid #ccc",
                borderRadius: "8px",
                p: { xs: 1, sm: 1.5, md: 2 },
                backgroundColor: "#e3f2fd",
                minHeight: { xs: "120px", sm: "150px", md: "200px" },
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
              }}>
                {tasks.length > 0 ? (
                  tasks
                    .filter((task) => task.status === "Completed")
                    .map((task) => (
                      <Box key={task.id} sx={{ 
                        p: { xs: 0.5, sm: 0.75, md: 1 }, 
                        borderBottom: "1px solid #ddd" 
                      }}>
                        <Typography 
                          variant="body1" 
                          fontWeight="bold"
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" } }}
                        >
                          {task.taskDescription}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="textSecondary"
                          sx={{ fontSize: { xs: "0.625rem", sm: "0.75rem", md: "0.875rem" } }}
                        >
                          Completed at {task.endTime && task.endTime.includes('T') 
                            ? formatTimeForDisplay(new Date(task.endTime))
                            : task.endTime}
                        </Typography>
                      </Box>
                    ))
                ) : (
                  <Typography 
                    variant="body1" 
                    color="error"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" } }}
                  >
                    No completed tasks.
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Footer />
        </Container>
      </Box>
    );
  };

export default AssignTimer;